import {
  BookOpen,
  ChevronDown,
  Coins,
  LogOut,
  GitPullRequest,
  History,
  LayoutDashboard,
  Menu,
  Moon,
  PlusCircle,
  Settings,
  ShieldCheck,
  Sun,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Notice } from '../components/ui/Feedback';
import { useConfig } from '../hooks/queries';
import { useMe, useSignOut } from '../hooks/useAuth';
import { usePageContextValue } from '../hooks/usePageContext';
import { useTheme } from '../hooks/useTheme';
import type { Chalk } from '../lib/chalk';
import { CHALK } from '../lib/chalk';
import { cn } from '../lib/cn';

/** Each section has its own chalk colour; its page title is underlined in the same colour. */
export const NAV: Array<{ to: string; label: string; icon: typeof LayoutDashboard; color: Chalk; end?: boolean }> = [
  { to: '/app', label: 'Dashboard', icon: LayoutDashboard, color: 'yellow', end: true },
  { to: '/app/pull-requests', label: 'Pull Requests', icon: GitPullRequest, color: 'blue' },
  { to: '/app/new', label: 'Start New Review', icon: PlusCircle, color: 'green' },
  { to: '/app/history', label: 'Review History', icon: History, color: 'pink' },
  { to: '/app/credits', label: 'Credits', icon: Coins, color: 'green' },
  { to: '/app/settings', label: 'Settings', icon: Settings, color: 'orange' },
  { to: '/app/docs', label: 'Documentation', icon: BookOpen, color: 'lilac' },
];

export function Logo({ compact = false }: { compact?: boolean }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border-2 border-dashed border-chalk-yellow/50 text-chalk-yellow">
        <ShieldCheck className="h-5 w-5" aria-hidden />
      </span>
      <span className={cn('chalk-title whitespace-nowrap leading-none text-ink', compact ? 'text-2xl' : 'text-[1.7rem]')}>
        PR <span className="text-chalk-yellow">Pilot</span>
      </span>
    </div>
  );
}

function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <nav aria-label="Main" className="flex flex-col gap-1 px-3 py-4">
      {NAV.map(({ to, label, icon: Icon, end, color }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-[0.9rem] font-medium transition-colors',
              isActive ? cn(CHALK[color].soft, 'text-ink') : 'text-muted hover:bg-raised/60 hover:text-ink',
            )
          }
        >
          {({ isActive }) => (
            <>
              {isActive && <span className={cn('absolute inset-y-2 left-0 w-1 rounded-full', CHALK[color].bg)} aria-hidden />}
              <Icon className={cn('h-[1.1rem] w-[1.1rem] shrink-0', CHALK[color].text)} aria-hidden />
              {label}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

/** Three chalk sticks resting on the ledge — the sidebar's only decoration. */
function ChalkSticks() {
  return (
    <div className="flex items-end gap-1.5 px-5 pb-1" aria-hidden>
      <span className="h-1.5 w-7 rounded-full bg-chalk-yellow/90" />
      <span className="h-1.5 w-5 rounded-full bg-chalk-pink/90" />
      <span className="h-1.5 w-6 rounded-full bg-chalk-blue/90" />
      <span className="h-1.5 w-4 rounded-full bg-ink/80" />
    </div>
  );
}

function ModeBanners() {
  const { data } = useConfig();
  if (!data) return null;
  const demoOnly = data.demoMode || !data.claude.configured;
  return (
    <div className="mx-auto w-full max-w-7xl space-y-2 px-4 pt-5 sm:px-6 lg:px-10">
      {demoOnly && (
        <Notice tone="warn" title={data.demoMode ? 'Demo mode' : 'AI Pilot is not configured — demo mode only'}>
          Only the sample Pull Requests can be reviewed, and their findings are deterministic sample data, not AI Pilot
          analysis.{' '}
          {data.demoMode ? 'Set DEMO_MODE=false on the server to review real PRs.' : 'Add the AI API key and model to the server settings to review real PRs.'}
        </Notice>
      )}
      {!data.storage.persistent && (
        <Notice tone="info" title="Data is not persisted">
          MONGODB_URI is not set, so reviews are kept in memory and disappear when the server restarts.
        </Notice>
      )}
    </div>
  );
}

function CreditsPill() {
  const { data: me } = useMe();
  if (!me) return null;
  const empty = me.credits === 0;
  return (
    <Link
      to="/app/credits"
      className={cn(
        'flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
        empty
          ? 'border-chalk-pink/50 bg-chalk-pink/10 text-chalk-pink hover:bg-chalk-pink/20'
          : 'border-chalk-green/40 bg-chalk-green/10 text-chalk-green hover:bg-chalk-green/20',
      )}
      title={empty ? 'No credits left' : 'Review credits'}
    >
      <Coins className="h-4 w-4" aria-hidden />
      <span>
        {me.credits} <span className="hidden sm:inline">credit{me.credits === 1 ? '' : 's'}</span>
      </span>
    </Link>
  );
}

function UserMenu() {
  const { data: me } = useMe();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  if (!me) return null;
  const initials = me.name
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-full py-1 pl-1 pr-2 text-sm hover:bg-raised/70"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-dashed border-chalk-lilac/60 bg-chalk-lilac/15 text-xs font-bold text-chalk-lilac">
          {initials || '?'}
        </span>
        <span className="hidden max-w-[10rem] truncate md:inline">{me.name}</span>
        <ChevronDown className="h-3.5 w-3.5 text-faint" aria-hidden />
      </button>
      {open && (
        <div role="menu" className="card absolute right-0 top-full z-40 mt-2 w-60 p-1.5 shadow-xl">
          <div className="px-3 py-2">
            <p className="truncate text-sm font-medium">{me.name}</p>
            <p className="truncate text-xs text-muted">{me.email}</p>
          </div>
          <div className="my-1 border-t border-dashed border-line" />
          <Link role="menuitem" to="/app/credits" onClick={() => setOpen(false)} className="flex items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-raised/70">
            <Coins className="h-4 w-4 text-chalk-green" aria-hidden /> Credits
          </Link>
          <Link role="menuitem" to="/app/settings" onClick={() => setOpen(false)} className="flex items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-raised/70">
            <Settings className="h-4 w-4 text-chalk-orange" aria-hidden /> Settings
          </Link>
          <button
            role="menuitem"
            type="button"
            onClick={() => signOut.mutate(undefined, { onSettled: () => navigate('/', { replace: true }) })}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-raised/70"
          >
            <LogOut className="h-4 w-4 text-chalk-pink" aria-hidden /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export function AppLayout() {
  const { theme, toggle } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const context = usePageContextValue();
  const location = useLocation();

  useEffect(() => setMobileOpen(false), [location.pathname]);

  return (
    <div className="flex min-h-full">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-surface focus:px-3 focus:py-2">
        Skip to content
      </a>

      {/* Desktop sidebar */}
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 lg:flex">
        <div className="flex min-w-0 flex-1 flex-col bg-surface/60">
          <div className="flex h-20 items-center px-4">
            <Logo />
          </div>
          <SidebarNav />
          <div className="mt-auto">
            <p className="px-5 pb-4 text-xs leading-relaxed text-faint">
              Findings are potential issues written by AI. Check each one before you act on it.
            </p>
            <ChalkSticks />
          </div>
        </div>
        <div className="chalk-ledge w-3 shrink-0" aria-hidden />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} aria-hidden />
          <aside className="relative flex h-full w-72 flex-col bg-canvas shadow-2xl">
            <div className="flex h-16 items-center justify-between px-4">
              <Logo compact />
              <button onClick={() => setMobileOpen(false)} className="rounded p-1.5 text-muted hover:bg-raised" aria-label="Close navigation">
                <X className="h-4 w-4" />
              </button>
            </div>
            <SidebarNav onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-dashed border-line bg-canvas/80 px-4 backdrop-blur-md sm:px-6 lg:px-8">
          <button
            onClick={() => setMobileOpen(true)}
            className="rounded p-1.5 text-muted hover:bg-raised lg:hidden"
            aria-label="Open navigation"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="lg:hidden">
            <Logo compact />
          </div>
          {context && (
            <div className="hidden min-w-0 items-center gap-2 rounded-full border border-chalk-blue/35 bg-chalk-blue/10 px-3 py-1 text-sm md:flex">
              <GitPullRequest className="h-3.5 w-3.5 shrink-0 text-chalk-blue" aria-hidden />
              <span className="truncate font-mono text-xs text-ink">{context}</span>
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={toggle}
              className="flex items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-muted hover:bg-raised/70 hover:text-ink"
              aria-label={theme === 'dark' ? 'Switch to whiteboard theme' : 'Switch to chalkboard theme'}
              title={theme === 'dark' ? 'Whiteboard theme' : 'Chalkboard theme'}
            >
              {theme === 'dark' ? <Sun className="h-4 w-4 text-chalk-yellow" /> : <Moon className="h-4 w-4 text-chalk-lilac" />}
              <span className="hidden sm:inline">{theme === 'dark' ? 'Whiteboard' : 'Chalkboard'}</span>
            </button>
            <CreditsPill />
            <UserMenu />
          </div>
        </header>

        <ModeBanners />
        <main id="main" className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 sm:px-6 lg:px-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
